'use client';
import React, { useState, useMemo } from 'react';
import { Search, Filter, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Edit2, Trash2, Eye, UserCheck, X, CheckSquare, Square, SlidersHorizontal,  } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import type { BookingStatus } from '@/components/ui/StatusBadge';
import { MOCK_BOOKINGS, SERVICE_NAMES, STATUS_OPTIONS, type BookingRow } from './bookingMockData';
import { toast } from 'sonner';
import StatusChangeDropdown from './StatusChangeDropdown';
import StaffAssignDropdown from './StaffAssignDropdown';

type SortKey = keyof BookingRow;
type SortDir = 'asc' | 'desc';

const PAGE_SIZE_OPTIONS = [8, 15, 25];

export default function BookingsTable() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterService, setFilterService] = useState('All Services');
  const [filterDate, setFilterDate] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rows, setRows] = useState<BookingRow[]>(MOCK_BOOKINGS);
  const [activeStatusDropdown, setActiveStatusDropdown] = useState<string | null>(null);
  const [activeStaffDropdown, setActiveStaffDropdown] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    let data = [...rows];
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(
        (r) =>
          r.customerName.toLowerCase().includes(q) ||
          r.ref.toLowerCase().includes(q) ||
          r.vehicle.toLowerCase().includes(q) ||
          r.service.toLowerCase().includes(q)
      );
    }
    if (filterStatus !== 'all') data = data.filter((r) => r.status === filterStatus);
    if (filterService !== 'All Services') data = data.filter((r) => r.service === filterService);
    if (filterDate) data = data.filter((r) => r.date === filterDate);
    data.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return data;
  }, [rows, search, filterStatus, filterService, filterDate, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginated.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(paginated.map((r) => r.id)));
  };

  const handleStatusChange = (id: string, newStatus: BookingStatus) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r)));
    setActiveStatusDropdown(null);
    toast.success(`Booking status updated to ${newStatus}`);
  };

  const handleStaffAssign = (id: string, staff: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, staff } : r)));
    setActiveStaffDropdown(null);
    toast.success(`Assigned to ${staff}`);
  };

  const handleBulkDelete = () => {
    const count = selectedIds.size;
    setRows((prev) => prev.filter((r) => !selectedIds.has(r.id)));
    setSelectedIds(new Set());
    toast.success(`${count} booking${count > 1 ? 's' : ''} deleted`);
  };

  const handleBulkConfirm = () => {
    const count = selectedIds.size;
    setRows((prev) => prev.map((r) => (selectedIds.has(r.id) ? { ...r, status: 'confirmed' as BookingStatus } : r)));
    setSelectedIds(new Set());
    toast.success(`${count} booking${count > 1 ? 's' : ''} confirmed`);
  };

  const SortIcon = ({ col }: { col: SortKey }) => (
    <span className="flex flex-col ml-1 opacity-40">
      <ChevronUp size={10} className={sortKey === col && sortDir === 'asc' ? 'opacity-100 text-primary' : ''} />
      <ChevronDown size={10} className={sortKey === col && sortDir === 'desc' ? 'opacity-100 text-primary' : ''} />
    </span>
  );

  const clearFilters = () => {
    setSearch('');
    setFilterStatus('all');
    setFilterService('All Services');
    setFilterDate('');
    setPage(1);
  };

  const hasActiveFilters = search || filterStatus !== 'all' || filterService !== 'All Services' || filterDate;

  return (
    <div className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
      {/* Table Header */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-700 text-foreground">All Bookings</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {filtered.length} booking{filtered.length !== 1 ? 's' : ''} found
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 sm:flex-none sm:w-64">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search by name, ref, vehicle..."
                className="w-full pl-8 pr-3 py-2 text-sm bg-muted rounded-lg border border-transparent focus:border-primary focus:bg-card outline-none transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-500 border transition-colors ${
                showFilters || hasActiveFilters ? 'bg-secondary text-primary border-primary/30' : 'bg-card text-muted-foreground border-border hover:text-foreground hover:bg-muted'
              }`}
            >
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="bg-primary text-primary-foreground text-[10px] font-700 rounded-full w-4 h-4 flex items-center justify-center">
                  !
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Row */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-3 items-center animate-fade-in">
            {/* Status Filter */}
            <div>
              <label className="block text-[10px] font-600 text-muted-foreground uppercase tracking-wider mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                className="input-field py-1.5 text-xs w-36"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={`status-opt-${s}`} value={s}>
                    {s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Service Filter */}
            <div>
              <label className="block text-[10px] font-600 text-muted-foreground uppercase tracking-wider mb-1">Service</label>
              <select
                value={filterService}
                onChange={(e) => { setFilterService(e.target.value); setPage(1); }}
                className="input-field py-1.5 text-xs w-44"
              >
                {SERVICE_NAMES.map((s) => (
                  <option key={`svc-opt-${s}`} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Date Filter */}
            <div>
              <label className="block text-[10px] font-600 text-muted-foreground uppercase tracking-wider mb-1">Date</label>
              <input
                type="date"
                value={filterDate}
                onChange={(e) => { setFilterDate(e.target.value); setPage(1); }}
                className="input-field py-1.5 text-xs w-40"
              />
            </div>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground font-500 mt-4 transition-colors"
              >
                <X size={12} />
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="px-5 py-3 bg-secondary border-b border-border flex items-center gap-3 animate-slide-up">
          <span className="text-sm font-600 text-primary">
            {selectedIds.size} selected
          </span>
          <div className="flex gap-2 ml-auto">
            <button
              onClick={handleBulkConfirm}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-600 hover:bg-primary/90 transition-colors"
            >
              <UserCheck size={13} />
              Confirm All
            </button>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-600 hover:bg-red-100 transition-colors"
            >
              <Trash2 size={13} />
              Delete Selected
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
              aria-label="Clear selection"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px]">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="w-10 px-4 py-3 text-left">
                <button onClick={toggleSelectAll} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Select all rows">
                  {selectedIds.size === paginated.length && paginated.length > 0 ? (
                    <CheckSquare size={15} className="text-primary" />
                  ) : (
                    <Square size={15} />
                  )}
                </button>
              </th>
              {[
                { key: 'ref' as SortKey, label: 'Ref #', w: 'w-24' },
                { key: 'customerName' as SortKey, label: 'Customer', w: 'w-40' },
                { key: 'service' as SortKey, label: 'Service', w: 'w-44' },
                { key: 'vehicle' as SortKey, label: 'Vehicle', w: 'w-40' },
                { key: 'date' as SortKey, label: 'Date', w: 'w-28' },
                { key: 'timeSlot' as SortKey, label: 'Time', w: 'w-24' },
                { key: 'price' as SortKey, label: 'Price', w: 'w-20' },
                { key: 'status' as SortKey, label: 'Status', w: 'w-32' },
                { key: 'staff' as SortKey, label: 'Staff', w: 'w-36' },
              ].map((col) => (
                <th
                  key={`th-${col.key}`}
                  className={`${col.w} px-4 py-3 text-left text-[11px] font-600 text-muted-foreground uppercase tracking-wider cursor-pointer select-none hover:text-foreground transition-colors`}
                  onClick={() => handleSort(col.key)}
                >
                  <span className="flex items-center">
                    {col.label}
                    <SortIcon col={col.key} />
                  </span>
                </th>
              ))}
              <th className="w-20 px-4 py-3 text-left text-[11px] font-600 text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                      <Search size={20} className="text-muted-foreground" />
                    </div>
                    <p className="text-sm font-600 text-foreground">No bookings found</p>
                    <p className="text-xs text-muted-foreground max-w-xs">
                      Try adjusting your search or filters. Bookings matching your criteria will appear here.
                    </p>
                    {hasActiveFilters && (
                      <button onClick={clearFilters} className="text-xs text-primary font-600 hover:underline mt-1">
                        Clear all filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((row, idx) => {
                const isSelected = selectedIds.has(row.id);
                return (
                  <tr
                    key={row.id}
                    className={`border-b border-border transition-colors group ${
                      isSelected ? 'bg-secondary/60' : idx % 2 === 0 ? 'bg-card hover:bg-muted/40' : 'bg-muted/10 hover:bg-muted/40'
                    }`}
                  >
                    {/* Checkbox */}
                    <td className="px-4 py-3">
                      <button onClick={() => toggleSelect(row.id)} className="text-muted-foreground hover:text-foreground transition-colors" aria-label={`Select booking ${row.ref}`}>
                        {isSelected ? (
                          <CheckSquare size={15} className="text-primary" />
                        ) : (
                          <Square size={15} />
                        )}
                      </button>
                    </td>

                    {/* Ref */}
                    <td className="px-4 py-3">
                      <span className="text-xs font-700 text-foreground font-mono tracking-wide">{row.ref}</span>
                    </td>

                    {/* Customer */}
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-600 text-foreground whitespace-nowrap">{row.customerName}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-[150px]">{row.customerEmail}</p>
                      </div>
                    </td>

                    {/* Service */}
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground whitespace-nowrap">{row.service}</p>
                      <p className="text-xs text-muted-foreground">{row.duration >= 60 ? `${Math.floor(row.duration / 60)}h${row.duration % 60 ? ` ${row.duration % 60}m` : ''}` : `${row.duration}m`}</p>
                    </td>

                    {/* Vehicle */}
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground whitespace-nowrap">{row.vehicle}</p>
                      <p className="text-xs text-muted-foreground">{row.vehicleType}</p>
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground tabular-nums whitespace-nowrap">
                        {row.date.slice(5).replace('-', '/')}
                      </span>
                    </td>

                    {/* Time */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground tabular-nums whitespace-nowrap">{row.timeSlot}</span>
                    </td>

                    {/* Price */}
                    <td className="px-4 py-3">
                      <span className="text-sm font-700 text-foreground tabular-nums">${row.price}</span>
                    </td>

                    {/* Status — inline dropdown */}
                    <td className="px-4 py-3 relative">
                      <button
                        onClick={() => setActiveStatusDropdown(activeStatusDropdown === row.id ? null : row.id)}
                        className="hover:opacity-80 transition-opacity"
                        aria-label={`Change status for ${row.ref}`}
                      >
                        <StatusBadge status={row.status} size="sm" />
                      </button>
                      {activeStatusDropdown === row.id && (
                        <StatusChangeDropdown
                          currentStatus={row.status}
                          onSelect={(s) => handleStatusChange(row.id, s)}
                          onClose={() => setActiveStatusDropdown(null)}
                        />
                      )}
                    </td>

                    {/* Staff */}
                    <td className="px-4 py-3 relative">
                      <button
                        onClick={() => setActiveStaffDropdown(activeStaffDropdown === row.id ? null : row.id)}
                        className={`text-sm font-500 transition-colors hover:text-primary whitespace-nowrap ${
                          row.staff ? 'text-foreground' : 'text-muted-foreground italic'
                        }`}
                        aria-label={`Assign staff for ${row.ref}`}
                      >
                        {row.staff ?? '+ Assign'}
                      </button>
                      {activeStaffDropdown === row.id && (
                        <StaffAssignDropdown
                          currentStaff={row.staff}
                          onSelect={(s) => handleStaffAssign(row.id, s)}
                          onClose={() => setActiveStaffDropdown(null)}
                        />
                      )}
                    </td>

                    {/* Row Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className="p-1.5 rounded-lg hover:bg-secondary hover:text-primary transition-colors text-muted-foreground"
                          title="View booking details"
                          aria-label={`View ${row.ref}`}
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="p-1.5 rounded-lg hover:bg-secondary hover:text-primary transition-colors text-muted-foreground"
                          title="Edit booking"
                          aria-label={`Edit ${row.ref}`}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          className="p-1.5 rounded-lg hover:bg-red-50 hover:text-red-500 transition-colors text-muted-foreground"
                          title="Delete booking — this cannot be undone"
                          aria-label={`Delete ${row.ref}`}
                          onClick={() => {
                            setRows((prev) => prev.filter((r) => r.id !== row.id));
                            toast.success(`Booking ${row.ref} deleted`);
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-5 py-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>
            Showing {filtered.length === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of{' '}
            <span className="font-600 text-foreground tabular-nums">{filtered.length}</span> bookings
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-xs">Rows:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="text-xs border border-border rounded-lg px-2 py-1 bg-card text-foreground outline-none focus:border-primary"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={`ps-${n}`} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Previous page"
            >
              <ChevronLeft size={15} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
              if (p === 1 || p === totalPages || Math.abs(p - page) <= 1) {
                return (
                  <button
                    key={`page-${p}`}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg text-sm font-600 transition-colors ${
                      page === p
                        ? 'bg-primary text-primary-foreground'
                        : 'border border-border hover:bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {p}
                  </button>
                );
              }
              if (Math.abs(p - page) === 2) {
                return <span key={`ellipsis-${p}`} className="text-muted-foreground text-sm px-1">…</span>;
              }
              return null;
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Next page"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}